import { create } from "zustand";
import { persist } from "zustand/middleware";

// ============================================================================
// Types
// ============================================================================

export type MarketView = "long" | "short" | "neutral";
export type RiskLevel = "low" | "medium" | "high";
export type StrategyStatus = "active" | "paused" | "closed";
export type AssetClass = "futures" | "crypto" | "equities" | "forex";
export type StrategyOwnerType = "sovrun" | "third_party";
export type ViewMode = "card" | "list";

export interface Strategy {
  id: string;
  name: string;
  slug: string;
  description: string;
  longDescription: string | null;

  // Classification
  marketView: MarketView;
  riskLevel: RiskLevel;
  assetClasses: AssetClass[];
  supportedAssets: string[];

  // Ownership / attribution (SFWL-010)
  ownerType?: StrategyOwnerType;
  ownerName?: string | null;
  ownerWebsite?: string | null;
  disclosureUrl?: string | null;
  ownerLogoUrl?: string | null;

  // Performance
  lifetimeReturn: number;
  yearReturn: number;
  monthReturn: number;
  weekReturn: number;

  // Risk Metrics
  sharpeRatio: number;
  maxDrawdown: number;
  winRate: number;
  avgTradeDurationHours: number;

  // AUM
  totalAum: number;
  aumChange24h: number;

  // Status
  status: StrategyStatus;
  launchDate: Date;

  // User allocation (if any)
  userAllocation: UserAllocation | null;
}

export interface UserAllocation {
  id: string;
  allocatedAmount: number;
  currentValue: number;
  shares: number;
  unrealizedPnl: number;
  unrealizedPnlPercent: number;
  allocatedAt: Date;
}

export interface AllocationSummary {
  id: string;
  strategyId: string;
  strategyName: string;
  allocatedAmount: number;
  currentValue: number;
  unrealizedPnl: number;
  unrealizedPnlPercent: number;
  realizedPnl: number;
  allocatedAt: Date;
}

export interface PerformanceSnapshot {
  timestamp: Date;
  cumulativeReturn: number;
  dailyReturn: number | null;
  aum: number | null;
  sharePrice: number;
}

// Filter & Sort
export type SortField =
  | "name"
  | "monthReturn"
  | "lifetimeReturn"
  | "yearReturn"
  | "totalAum";
export type SortOrder = "asc" | "desc";

export interface StrategyFilters {
  marketView: MarketView | "all";
  riskLevel: RiskLevel | "all";
  assetClass: AssetClass | "all";
}

// ============================================================================
// Store State
// ============================================================================

interface StrategiesState {
  // Data
  strategies: Strategy[];
  userAllocations: AllocationSummary[];
  selectedStrategy: Strategy | null;
  performanceData: PerformanceSnapshot[];

  // Filters & Sort
  filters: StrategyFilters;
  sortField: SortField;
  sortOrder: SortOrder;

  // Loading states
  isLoadingStrategies: boolean;
  isLoadingAllocations: boolean;
  isLoadingPerformance: boolean;
  isAllocating: boolean;

  // UI State
  viewMode: ViewMode;
  allocationModalOpen: boolean;
  allocationModalStrategyId: string | null;
  manageModalOpen: boolean;
  manageModalStrategyId: string | null;

  // Error
  error: string | null;

  // Actions - Data
  setStrategies: (strategies: Strategy[]) => void;
  setUserAllocations: (allocations: AllocationSummary[]) => void;
  setSelectedStrategy: (strategy: Strategy | null) => void;
  setPerformanceData: (data: PerformanceSnapshot[]) => void;
  updateStrategyAllocation: (
    strategyId: string,
    allocation: UserAllocation | null
  ) => void;

  // Actions - Filters
  setFilters: (filters: Partial<StrategyFilters>) => void;
  setSortField: (field: SortField) => void;
  setSortOrder: (order: SortOrder) => void;
  resetFilters: () => void;

  // Actions - Loading
  setLoadingStrategies: (loading: boolean) => void;
  setLoadingAllocations: (loading: boolean) => void;
  setLoadingPerformance: (loading: boolean) => void;
  setAllocating: (allocating: boolean) => void;

  // Actions - View
  setViewMode: (mode: ViewMode) => void;

  // Actions - Modals
  openAllocationModal: (strategyId: string) => void;
  closeAllocationModal: () => void;
  openManageModal: (strategyId: string) => void;
  closeManageModal: () => void;

  // Actions - Error
  setError: (error: string | null) => void;
  clearError: () => void;

  // Actions - Reset
  reset: () => void;
}

// ============================================================================
// Default Values
// ============================================================================

const DEFAULT_FILTERS: StrategyFilters = {
  marketView: "all",
  riskLevel: "all",
  assetClass: "all",
};

// ============================================================================
// Initial Data
// ============================================================================

// Note: Strategies are now fetched from the API via useStrategies hook.
// The store initializes with an empty array and is populated by API data.

// ============================================================================
// Store
// ============================================================================

export const useStrategiesStore = create<StrategiesState>()(
  persist(
    (set) => ({
      // Initial state - strategies loaded from API via useStrategies hook
      strategies: [],
      userAllocations: [],
      selectedStrategy: null,
      performanceData: [],

      filters: DEFAULT_FILTERS,
      sortField: "totalAum",
      sortOrder: "desc",

      isLoadingStrategies: false,
      isLoadingAllocations: false,
      isLoadingPerformance: false,
      isAllocating: false,

      viewMode: "card" as ViewMode,

      allocationModalOpen: false,
      allocationModalStrategyId: null,
      manageModalOpen: false,
      manageModalStrategyId: null,

      error: null,

      // Actions - Data
      setStrategies: (strategies) => set({ strategies }),
      setUserAllocations: (allocations) => set({ userAllocations: allocations }),
      setSelectedStrategy: (strategy) => set({ selectedStrategy: strategy }),
      setPerformanceData: (data) => set({ performanceData: data }),
      updateStrategyAllocation: (strategyId, allocation) =>
        set((state) => ({
          strategies: state.strategies.map((s) =>
            s.id === strategyId ? { ...s, userAllocation: allocation } : s
          ),
        })),

      // Actions - Filters
      setFilters: (filters) =>
        set((state) => ({
          filters: { ...state.filters, ...filters },
        })),
      setSortField: (field) => set({ sortField: field }),
      setSortOrder: (order) => set({ sortOrder: order }),
      resetFilters: () => set({ filters: DEFAULT_FILTERS }),

      // Actions - Loading
      setLoadingStrategies: (loading) => set({ isLoadingStrategies: loading }),
      setLoadingAllocations: (loading) => set({ isLoadingAllocations: loading }),
      setLoadingPerformance: (loading) => set({ isLoadingPerformance: loading }),
      setAllocating: (allocating) => set({ isAllocating: allocating }),

      // Actions - View
      setViewMode: (mode) => set({ viewMode: mode }),

      // Actions - Modals
      openAllocationModal: (strategyId) =>
        set({
          allocationModalOpen: true,
          allocationModalStrategyId: strategyId,
        }),
      closeAllocationModal: () =>
        set({ allocationModalOpen: false, allocationModalStrategyId: null }),
      openManageModal: (strategyId) =>
        set({ manageModalOpen: true, manageModalStrategyId: strategyId }),
      closeManageModal: () =>
        set({ manageModalOpen: false, manageModalStrategyId: null }),

      // Actions - Error
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // Actions - Reset
      reset: () =>
        set({
          strategies: [],
          userAllocations: [],
          selectedStrategy: null,
          performanceData: [],
          error: null,
        }),
    }),
    {
      name: "strategies-store",
      version: 1,
      partialize: (state) => ({
        // Only persist user preferences
        filters: state.filters,
        sortField: state.sortField,
        sortOrder: state.sortOrder,
        viewMode: state.viewMode,
      }),
      migrate: (persisted: unknown) => {
        const state = persisted as Record<string, unknown>;
        // Normalize stale filter values (empty strings → "all")
        if (state.filters) {
          const filters = state.filters as Record<string, string>;
          if (!filters.marketView) filters.marketView = "all";
          if (!filters.riskLevel) filters.riskLevel = "all";
          if (!filters.assetClass) filters.assetClass = "all";
        }
        return state;
      },
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectFilteredStrategies = (state: StrategiesState): Strategy[] => {
  let filtered = [...state.strategies];

  // Apply filters (guard against empty strings from stale persisted state)
  if (state.filters.marketView && state.filters.marketView !== "all") {
    filtered = filtered.filter((s) => s.marketView === state.filters.marketView);
  }
  if (state.filters.riskLevel && state.filters.riskLevel !== "all") {
    filtered = filtered.filter((s) => s.riskLevel === state.filters.riskLevel);
  }
  if (state.filters.assetClass && state.filters.assetClass !== "all") {
    filtered = filtered.filter((s) =>
      s.assetClasses?.includes(state.filters.assetClass as AssetClass)
    );
  }

  // Apply sort
  filtered.sort((a, b) => {
    let aVal: number | string;
    let bVal: number | string;

    switch (state.sortField) {
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "lifetimeReturn":
        aVal = a.lifetimeReturn;
        bVal = b.lifetimeReturn;
        break;
      case "yearReturn":
        aVal = a.yearReturn;
        bVal = b.yearReturn;
        break;
      case "totalAum":
      default:
        aVal = a.totalAum;
        bVal = b.totalAum;
    }

    if (state.sortOrder === "asc") {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });

  return filtered;
};

export const selectTotalAllocated = (state: StrategiesState): number => {
  return state.userAllocations.reduce((sum, a) => sum + a.allocatedAmount, 0);
};

export const selectTotalUnrealizedPnl = (state: StrategiesState): number => {
  return state.userAllocations.reduce((sum, a) => sum + a.unrealizedPnl, 0);
};

export const selectStrategyById = (
  state: StrategiesState,
  id: string
): Strategy | undefined => {
  return state.strategies.find((s) => s.id === id);
};
